/**
 * Utility to detect CPR version from uploaded Excel file headers
 */

/**
 * Detects the CPR version based on the headers/columns present in the uploaded data
 * @param {Array} data - Array of data objects from the Excel file
 * @param {string} fileType - Type of file: 'timesheet' or 'payroll'
 * @returns {{ version: string | null, confidence: string }}
 */

// CPR Version Constants
export const CPR_VERSIONS = Object.freeze({
  V1: '9/30/2026',
  V2: '1/31/2028',
});

// Confidence Constants
export const CONFIDENCE = Object.freeze({
  HIGH: 'high',
  MEDIUM: 'medium',
  UNKNOWN: 'unknown',
});

// Version match reasons
export const MATCH_REASON = Object.freeze({
  EXACT_MATCH: 'exact-match',
  UNDETERMINED: 'undetermined',
  VERSION_MISMATCH: 'version-mismatch',
});

// Normalize version strings for safe comparison
const normalizeVersionString = (value = '') => value.toLowerCase().replace(/\s+/g, '');

/** Generic detector based on indicators and thresholds **/
const detectVersionByIndicators = ({headers, v1Indicators, v2Indicators, v1HighThreshold, v2HighThreshold,}) => {
  const v2Matches = v2Indicators.filter(i => headers.includes(i)).length;
  const v1Matches = v1Indicators.filter(i => headers.includes(i)).length;

  if (v2Matches >= v2HighThreshold) {
    return {
      version: `Version Expiring ${CPR_VERSIONS.V2}`,
      confidence: CONFIDENCE.HIGH,
    };
  }

  if (v1Matches >= v1HighThreshold) {
    return {
      version: `Version Expiring ${CPR_VERSIONS.V1}`,
      confidence: CONFIDENCE.HIGH,
    };
  }

  if (v2Matches > v1Matches) {
    return {
      version: `Version Expiring ${CPR_VERSIONS.V2}`,
      confidence: CONFIDENCE.MEDIUM,
    };
  }

  if (v1Matches > 0) {
    return {
      version: `Version Expiring ${CPR_VERSIONS.V1}`,
      confidence: CONFIDENCE.MEDIUM,
    };
  }

  return { version: null, confidence: CONFIDENCE.UNKNOWN };
};

/** Detection configuration per file type **/
const DETECTION_CONFIG = Object.freeze({
  timesheet: {
    v2Indicators: ['labor classification'],
    v1Indicators: [
      'email',
      'standard pay rate',
      'over time pay rate',
      'per diem',
      'position',
    ],
    v2HighThreshold: 1,
    v1HighThreshold: 3,
  },
  payroll: {
    v2Indicators: [
      'regular hours',
      'regular pay rate',
      'over time hours',
      'over time pay rate',
      'total fringe benefit credit',
      'payment in lieu of fringe benefits',
    ],
    v1Indicators: [
      'paycheck number',
      'rate',
      'total hours worked',
      'total pay',
    ],
    v2HighThreshold: 3,
    v1HighThreshold: 2,
  },
});


/** Detect CPR version from uploaded file **/
export const detectFileVersion = (data, fileType) => {
  if (!data || data.length === 0) {
    return { version: null, confidence: CONFIDENCE.UNKNOWN };
  }

  // Get the headers from the first row
  const headers = Object.keys(data[0]).map(h => h.toLowerCase().trim());
  
  const config = DETECTION_CONFIG[fileType];
  if (!config) {
    return { version: null, confidence: CONFIDENCE.UNKNOWN };
  }
  return detectVersionByIndicators({
    headers,
    ...config,
  });
};

/**
 * Gets a friendly version label for display
 * @param {string} cprVersion - The CPR version from project info
 * @returns {string} - User-friendly version label
 */
export const getVersionLabel = (cprVersion) => {
  if (!cprVersion) return 'Unknown';

  const normalized = normalizeVersionString(cprVersion);
  
  if (normalized.includes(CPR_VERSIONS.V2)) {
    return `Version Expiring ${CPR_VERSIONS.V2}`;
  }
  
  if (normalized.includes(CPR_VERSIONS.V1)) {
    return `Version Expiring ${CPR_VERSIONS.V1}`;
  }
  
  return cprVersion;
};

/**
 * Checks if file version matches project CPR version
 * @param {string} fileVersion - Detected version from file
 * @param {string} projectCprVersion - CPR version from project
 * @returns {Object} - { match: boolean, reason?: string }
 */
export const versionsMatch = (fileVersion, projectCprVersion) => {
  if (!fileVersion || !projectCprVersion) {
    return { match: true, reason: MATCH_REASON.UNDETERMINED }; // Can't determine, allow upload but flag
  }

  const file = normalizeVersionString(fileVersion);
  const project = normalizeVersionString(projectCprVersion);
  
  // Check if both contain the same expiration date
  if (file.includes(CPR_VERSIONS.V2) && project.includes(CPR_VERSIONS.V2)) {
    return { match: true, reason: MATCH_REASON.EXACT_MATCH };
  }
  
  if (file.includes(CPR_VERSIONS.V1) && project.includes(CPR_VERSIONS.V1)) {
    return { match: true, reason: MATCH_REASON.EXACT_MATCH };
  }
  
  return { match: false, reason: MATCH_REASON.VERSION_MISMATCH};
};

